import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";

const MARKER_ICONS = {
  abastecimiento: { color: "#22c55e", label: "A" },
  control: { color: "#3b82f6", label: "C" },
  corte: { color: "#ef4444", label: "X" },
  partida: { color: "#FF4D00", label: "P" },
  meta: { color: "#a855f7", label: "M" },
  otro: { color: "#f59e0b", label: "?" },
};

function createIcon(type) {
  const cfg = MARKER_ICONS[type] || MARKER_ICONS.otro;
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="background:${cfg.color};color:#fff;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)">${cfg.label}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

function FitBounds({ track }) {
  const map = useMap();
  useEffect(() => {
    if (track && track.length > 1) {
      const bounds = L.latLngBounds(track.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [map, track]);
  return null;
}

export default function TrackMap({ track = [], markers = [], center, zoom = 13, height = "h-72", onClick, editable = false }) {
  const defaultCenter = track.length > 0
    ? [track[Math.floor(track.length / 2)].lat, track[Math.floor(track.length / 2)].lng]
    : center || [-45.57, -72.07];

  const positions = track.map(p => [p.lat, p.lng]);

  return (
    <div className={`${height} w-full relative z-0`} data-testid="track-map">
      <MapContainer
        center={defaultCenter}
        zoom={zoom}
        className="h-full w-full"
        scrollWheelZoom={true}
        style={{ background: "#1A1A1A" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {positions.length > 1 && (
          <>
            <Polyline positions={positions} pathOptions={{ color: "#FF4D00", weight: 3, opacity: 0.9 }} />
            <FitBounds track={track} />
          </>
        )}
        {markers.map((m, idx) => (
          <Marker key={idx} position={[m.lat, m.lng]} icon={createIcon(m.type)}>
            <Popup>
              <div className="text-xs">
                <strong>{m.name || m.type}</strong>
                {m.km && <span className="block text-gray-500">km {m.km}</span>}
                {m.description && <span className="block">{m.description}</span>}
              </div>
            </Popup>
          </Marker>
        ))}
        {editable && <MapClickHandler onClick={onClick} />}
      </MapContainer>
      {/* Legend */}
      {markers.length > 0 && (
        <div className="absolute bottom-2 left-2 bg-black/80 backdrop-blur-sm px-3 py-2 z-[1000] border border-white/10">
          <div className="flex flex-wrap gap-3 text-xs">
            {Object.entries(MARKER_ICONS).filter(([k]) => markers.some(m => m.type === k)).map(([key, cfg]) => (
              <span key={key} className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full" style={{ background: cfg.color }} />
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MapClickHandler({ onClick }) {
  const map = useMap();
  useEffect(() => {
    if (!onClick) return;
    const handler = (e) => onClick(e.latlng);
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [map, onClick]);
  return null;
}

export function StartLocationMap({ lat, lng, name, height = "h-48" }) {
  if (!lat || !lng) return null;
  return (
    <div className={`${height} w-full`} data-testid="start-location-map">
      <MapContainer center={[lat, lng]} zoom={14} className="h-full w-full" scrollWheelZoom={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Marker position={[lat, lng]} icon={createIcon("partida")}>
          <Popup><strong>{name || "Punto de Partida"}</strong></Popup>
        </Marker>
      </MapContainer>
    </div>
  );
}
